import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex items-center justify-between gap-2" }
const _hoisted_3 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "mt-2 grid grid-cols-3 gap-2 tabular-nums" }
const _hoisted_6 = { class: "rounded-md px-2 py-1.5" }
const _hoisted_7 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_8 = { class: "mt-0.5 text-[22px] font-extrabold leading-tight tracking-tight text-gray-900" }
const _hoisted_9 = {
  key: 0,
  class: "text-[11px] text-gray-400"
}
const _hoisted_10 = ["aria-pressed"]
const _hoisted_11 = { class: "text-[10px] font-bold uppercase tracking-[0.5px]" }
const _hoisted_12 = {
  key: 0,
  class: "text-[11px] text-gray-400"
}
const _hoisted_13 = {
  key: 1,
  class: "flex items-center gap-1 text-[11px] text-gray-400"
}
const _hoisted_14 = ["aria-pressed"]
const _hoisted_15 = { class: "text-[10px] font-bold uppercase tracking-[0.5px]" }
const _hoisted_16 = {
  key: 0,
  class: "text-[11px] text-gray-400"
}
const _hoisted_17 = {
  key: 0,
  class: "mt-3 rounded-md bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800"
}
const _hoisted_18 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_19 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_20 = { class: "mt-1 w-full text-sm tabular-nums" }
const _hoisted_21 = { class: "border-b border-gray-200 text-[10px] font-bold uppercase tracking-[0.5px]" }
const _hoisted_22 = { class: "divide-y divide-gray-100" }
const _hoisted_23 = { class: "py-2 text-xs text-gray-400" }
const _hoisted_24 = { class: "py-2 text-xs text-gray-400" }
const _hoisted_25 = { class: "py-2 text-xs text-gray-400" }
const _hoisted_26 = {
  key: 0,
  class: "mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-gray-100 px-3 py-2 tabular-nums"
}
const _hoisted_27 = { class: "text-center" }
const _hoisted_28 = { class: "text-sm font-bold leading-tight text-gray-800" }
const _hoisted_29 = { class: "text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_30 = { class: "text-center" }
const _hoisted_31 = { class: "text-sm font-bold leading-tight text-gray-800" }
const _hoisted_32 = { class: "text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_33 = { class: "text-center" }
const _hoisted_34 = { class: "text-sm font-bold leading-tight text-gray-800" }
const _hoisted_35 = { class: "text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_36 = {
  key: 1,
  class: "mt-1.5 text-[11px] text-gray-400"
}

import { computed, ref, watch } from 'vue';
import { Settings, TrendingDown, TrendingUp } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import { usePigTallySeries } from '@/plugins/store/actions/queries/active-groups';
import type { MortalityWindow } from '@/types/SectionLiveKpi';
import { mortalityAtOrAboveTargetForView, mortalityPercentForView, type MortalityView, type SectionRow } from './sectionRow';
import { INDEX_COLOUR_NEUTRAL } from './indexColour';
import { formatPigsToTarget, pigsToTargetColourClass } from '@/views/dashboard/overview/objectives/objectiveDisplay';
import DetailModalShell from './DetailModalShell.vue';
import PigTallyChart from './PigTallyChart.vue';

const placeholder = '—';

type SuppressionReason = NonNullable<SectionRow['mortalitySuppressionReason']>;


export default /*@__PURE__*/_defineComponent({
  __name: 'MortalityDetailModal',
  props: {
    section: {},
    view: {},
    sections: {}
  },
  emits: ["close", "navigate", "handleAlarm", "editTarget"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

function onNavigate(target: SectionRow): void {
    emit('navigate', target);
}

function onHandleAlarm(target: SectionRow): void {
    emit('handleAlarm', target);
}

// Feeds the week-over-week dead-count trend below (deadTrend) - not chart rendering, which is
// PigTallyChart's own concern. Tanstack Query dedupes this against PigTallyChart's identical
// query key, so this doesn't cost a second request.
const sectionId = computed(() => props.section?.id ?? null);
const { data: tallyData } = usePigTallySeries(sectionId);
const tallySeries = computed(() => tallyData.value ?? []);

// Same red/green scheme as the Døde cell: red at or above target, green below, neutral (no colour)
// when the target comparison itself is unknown - that must not silently read as "all good".
function rateClass(atOrAboveTarget: boolean | null): string {
    if (atOrAboveTarget === null) return INDEX_COLOUR_NEUTRAL;
    return atOrAboveTarget ? 'text-red-600' : 'text-green-700';
}

// The modal's own scope selection: seeded from the page-level view on open, then switched by
// clicking the stat blocks. Survives prev/next navigation and ignores page-toggle changes while
// open; each open re-adopts the page-level view (the component stays mounted between opens).
const modalView = ref<MortalityView>(props.view);

watch(
    () => props.section,
    (section, previous) => {
        if (section && !previous) modalView.value = props.view;
    }
);

// The stat block / table column matching the modal's view gets a visible fill, and its label
// echoes the page toggle's active state (brand pill) so the selection is self-explanatory.
// Inactive blocks get a lighter hover fill so they read as clickable.
function statBlockClass(view: MortalityView): string {
    return modalView.value === view ? 'bg-gray-100' : 'hover:bg-gray-100/60';
}

function statLabelClass(view: MortalityView): string {
    return modalView.value === view ? 'inline-block rounded bg-primary-700 px-1.5 py-0.5 leading-none text-white' : 'text-gray-400';
}

function columnClass(column: MortalityView): string {
    return modalView.value === column ? 'bg-gray-100 text-gray-900' : 'text-gray-600';
}

// The active window's inputs feed the formula strip under the calculation table.
const activeWindow = computed(() => (modalView.value === 'cumulative' ? props.section?.mortalityCumulative : props.section?.mortalityRolling) ?? null);
const activePercent = computed(() => (props.section ? mortalityPercentForView(props.section, modalView.value) : null));
const activeAtOrAboveTarget = computed(() => (props.section ? mortalityAtOrAboveTargetForView(props.section, modalView.value) : null));

// Target with a pass/fail marker per window; dash when no target is configured.
function targetLabel(atOrAboveTarget: boolean | null): string {
    const target = props.section?.mortalityTargetPercent ?? null;
    if (target === null) return placeholder;
    const marker = atOrAboveTarget === null ? '' : atOrAboveTarget ? ' ✕' : ' ✓';
    return `${i18n.global.n(target, 'decimal')} %${marker}`;
}

function windowDaysLabel(window: MortalityWindow | null): string {
    if (!window) return placeholder;
    return `${window.window_days} ${i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.daysUnit')}`;
}

// Maps the backend-computed suppression reason to a user-facing explanation of why the active
// view's percent is a dash. Empty when a figure is available (nothing to explain). Keyed by the
// union rather than `string`, so adding a wire value without a message here is a compile error
// instead of a silently blank panel.
const suppressionReasonMessages: Record<SuppressionReason, string> = {
    NoActiveProductionGroup: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.noActiveProductionGroup',
    DaysInPiggeryUnavailable: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.daysInPiggeryUnavailable',
    InsufficientPigsInWindow: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.insufficientPigsInWindow',
    PigMovementsNotRegisteredPerSection: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.pigMovementsNotRegisteredPerSection',
};

const suppressionReasonText = computed(() => {
    const reason = props.section?.mortalitySuppressionReason;
    const key = reason ? suppressionReasonMessages[reason] : undefined;
    return key ? i18n.global.t(key) : '';
});

// Week-over-week dead trend: raw dead counts summed over the series' last 7 daily points vs the
// 7 before them (the series ends today). Null until both windows are fully covered.
const deadTrend = computed(() => {
    const series = tallySeries.value;
    if (series.length < 14) return null;
    const sumDead = (points: typeof series) => points.reduce((sum, point) => sum + point.dead, 0);
    return {
        current: sumDead(series.slice(-7)),
        previous: sumDead(series.slice(-14, -7)),
    };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DetailModalShell, {
    section: _ctx.section,
    sections: _ctx.sections,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (emit('close'))),
    onNavigate: onNavigate,
    onHandleAlarm: onHandleAlarm
  }, {
    default: _withCtx(({ section: activeSection }) => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.title')), 1),
          _createElementVNode("button", {
            type: "button",
            class: "inline-flex shrink-0 items-center gap-1 rounded-md border border-primary-700 px-2 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:bg-gray-50",
            onClick: ($event: any) => (emit('editTarget', activeSection))
          }, [
            _createVNode(_unref(Settings), {
              class: "h-3 w-3",
              "aria-hidden": "true"
            }),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.editTarget')), 1)
          ], 8, _hoisted_4)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.tables.headings.age')), 1),
            _createElementVNode("p", _hoisted_8, _toDisplayString(activeSection.day !== null ? `${_ctx.$t('views.monitoring.activeGroups.texts.week')} ${activeSection.week}` : placeholder), 1),
            (activeSection.day !== null)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.texts.day')) + " " + _toDisplayString(activeSection.day), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["cursor-pointer rounded-md px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700", statBlockClass('rolling')]),
            "aria-pressed": modalView.value === 'rolling',
            onClick: _cache[0] || (_cache[0] = ($event: any) => (modalView.value = 'rolling'))
          }, [
            _createElementVNode("p", _hoisted_11, [
              _createElementVNode("span", {
                class: _normalizeClass(statLabelClass('rolling'))
              }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.mortalityView.rolling')), 3)
            ]),
            _createElementVNode("p", {
              class: _normalizeClass(["mt-0.5 flex items-baseline gap-1 text-[22px] font-extrabold leading-tight tracking-tight", rateClass(activeSection.mortalityAtOrAboveTarget)])
            }, [
              _createElementVNode("span", null, _toDisplayString(activeSection.mortalityPercent !== null ? `${_ctx.$n(activeSection.mortalityPercent, 'decimal')} %` : placeholder), 1),
              (activeSection.mortalityRolling?.pigs_to_target != null)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass(["text-[13px] font-semibold", _unref(pigsToTargetColourClass)(activeSection.mortalityRolling.pigs_to_target)])
                  }, "(" + _toDisplayString(_unref(formatPigsToTarget)(activeSection.mortalityRolling.pigs_to_target)) + ")", 3))
                : _createCommentVNode("", true)
            ], 2),
            (activeSection.mortalityTargetPercent !== null)
              ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.target')) + " " + _toDisplayString(targetLabel(activeSection.mortalityAtOrAboveTarget)), 1))
              : _createCommentVNode("", true),
            (deadTrend.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_13, [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.previousWindow')) + ": " + _toDisplayString(deadTrend.value.previous) + " ", 1),
                  (deadTrend.value.current > deadTrend.value.previous)
                    ? (_openBlock(), _createBlock(_unref(TrendingUp), {
                        key: 0,
                        class: "h-3.5 w-3.5 shrink-0 text-red-600",
                        "aria-hidden": "true"
                      }))
                    : (deadTrend.value.current < deadTrend.value.previous)
                      ? (_openBlock(), _createBlock(_unref(TrendingDown), {
                          key: 1,
                          class: "h-3.5 w-3.5 shrink-0 text-green-700",
                          "aria-hidden": "true"
                        }))
                      : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 10, _hoisted_10),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["cursor-pointer rounded-md px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700", statBlockClass('cumulative')]),
            "aria-pressed": modalView.value === 'cumulative',
            onClick: _cache[1] || (_cache[1] = ($event: any) => (modalView.value = 'cumulative'))
          }, [
            _createElementVNode("p", _hoisted_15, [
              _createElementVNode("span", {
                class: _normalizeClass(statLabelClass('cumulative'))
              }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.mortalityView.cumulative')), 3)
            ]),
            _createElementVNode("p", {
              class: _normalizeClass(["mt-0.5 flex items-baseline gap-1 text-[22px] font-extrabold leading-tight tracking-tight", rateClass(activeSection.mortalityCumulativeAtOrAboveTarget)])
            }, [
              _createElementVNode("span", null, _toDisplayString(activeSection.mortalityCumulativePercent !== null ? `${_ctx.$n(activeSection.mortalityCumulativePercent, 'decimal')} %` : placeholder), 1),
              (activeSection.mortalityCumulative?.pigs_to_target != null)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass(["text-[13px] font-semibold", _unref(pigsToTargetColourClass)(activeSection.mortalityCumulative.pigs_to_target)])
                  }, "(" + _toDisplayString(_unref(formatPigsToTarget)(activeSection.mortalityCumulative.pigs_to_target)) + ")", 3))
                : _createCommentVNode("", true)
            ], 2),
            (activeSection.mortalityTargetPercent !== null)
              ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.target')) + " " + _toDisplayString(targetLabel(activeSection.mortalityCumulativeAtOrAboveTarget)), 1))
              : _createCommentVNode("", true)
          ], 10, _hoisted_14)
        ]),
        (_unref(mortalityPercentForView)(activeSection, modalView.value) === null && suppressionReasonText.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(suppressionReasonText.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_18, [
        _createElementVNode("h3", _hoisted_19, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.figuresTitle')), 1),
        _createElementVNode("table", _hoisted_20, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", _hoisted_21, [
              _cache[3] || (_cache[3] = _createElementVNode("th", { class: "py-1.5 text-left font-bold" }, null, -1)),
              _createElementVNode("th", {
                class: _normalizeClass(["px-3 py-1.5 text-right", columnClass('rolling')])
              }, [
                _createElementVNode("span", {
                  class: _normalizeClass(statLabelClass('rolling'))
                }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.mortalityView.rolling')), 3)
              ], 2),
              _createElementVNode("th", {
                class: _normalizeClass(["px-3 py-1.5 text-right", columnClass('cumulative')])
              }, [
                _createElementVNode("span", {
                  class: _normalizeClass(statLabelClass('cumulative'))
                }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.mortalityView.cumulative')), 3)
              ], 2)
            ])
          ]),
          _createElementVNode("tbody", _hoisted_22, [
            _createElementVNode("tr", null, [
              _createElementVNode("td", _hoisted_23, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.dead')), 1),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('rolling')])
              }, _toDisplayString(activeSection.mortalityRolling?.dead ?? placeholder), 3),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('cumulative')])
              }, _toDisplayString(activeSection.mortalityCumulative?.dead ?? placeholder), 3)
            ]),
            _createElementVNode("tr", null, [
              _createElementVNode("td", _hoisted_24, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.avgPigs')), 1),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('rolling')])
              }, _toDisplayString(activeSection.mortalityRolling ? _ctx.$n(activeSection.mortalityRolling.avg_pigs, 'decimal') : placeholder), 3),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('cumulative')])
              }, _toDisplayString(activeSection.mortalityCumulative ? _ctx.$n(activeSection.mortalityCumulative.avg_pigs, 'decimal') : placeholder), 3)
            ]),
            _createElementVNode("tr", null, [
              _createElementVNode("td", _hoisted_25, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.windowDays')), 1),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('rolling')])
              }, _toDisplayString(windowDaysLabel(activeSection.mortalityRolling)), 3),
              _createElementVNode("td", {
                class: _normalizeClass(["px-3 py-2 text-right text-[15px] font-semibold", columnClass('cumulative')])
              }, _toDisplayString(windowDaysLabel(activeSection.mortalityCumulative)), 3)
            ])
          ])
        ]),
        (activeWindow.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("p", _hoisted_28, _toDisplayString(activeWindow.value.dead), 1),
                _createElementVNode("p", _hoisted_29, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.formulaDead')), 1)
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-300" }, "÷", -1)),
              _createElementVNode("div", _hoisted_30, [
                _createElementVNode("p", _hoisted_31, _toDisplayString(_ctx.$n(activeWindow.value.avg_pigs, 'decimal')), 1),
                _createElementVNode("p", _hoisted_32, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.formulaAvgPigs')), 1)
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-300" }, "×", -1)),
              _createElementVNode("div", _hoisted_33, [
                _createElementVNode("p", _hoisted_34, [
                  _createTextVNode(_toDisplayString(activeSection.mortalityDaysInPiggery !== null ? _ctx.$n(activeSection.mortalityDaysInPiggery, 'decimal') : placeholder), 1),
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-gray-400" }, " / ", -1)),
                  _createTextVNode(_toDisplayString(activeWindow.value.window_days), 1)
                ]),
                _createElementVNode("p", _hoisted_35, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.formulaFactor')), 1)
              ]),
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-300" }, "=", -1)),
              _createElementVNode("p", {
                class: _normalizeClass(["text-base font-extrabold leading-tight", rateClass(activeAtOrAboveTarget.value)])
              }, _toDisplayString(activePercent.value !== null ? `${_ctx.$n(activePercent.value, 'decimal')} %` : placeholder), 3)
            ]))
          : _createCommentVNode("", true),
        (activeSection.mortalityDaysInPiggery !== null)
          ? (_openBlock(), _createElementBlock("p", _hoisted_36, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggery')) + ": " + _toDisplayString(_ctx.$n(activeSection.mortalityDaysInPiggery, 'decimal')) + " — " + _toDisplayString(activeSection.mortalityDaysInPiggeryDerived
                            ? _ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggeryDerived')
                            : _ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggeryStandard')), 1))
          : _createCommentVNode("", true)
      ]),
      _createVNode(PigTallyChart, {
        "section-id": activeSection.id,
        day: activeSection.day,
        "directory-local-now": activeSection.directoryLocalNow
      }, null, 8, ["section-id", "day", "directory-local-now"])
    ]),
    _: 1
  }, 8, ["section", "sections"]))
}
}

})